// Reports.jsx - Enhanced with integrated filters, modern table, and member dropdown in search
import { useState, useEffect, useContext, useMemo, useRef } from "react";
import * as XLSX from "xlsx";
import { AuthContext } from "../context/AuthContext";
import { fetchWorkspaceReports, fetchWorkspaces } from "../api/api";
import "./Reports.css";

// Convert decimal hours to HH:MM:SS
const hoursToHMS = (decimalHours) => {
  const totalSeconds = Math.round(decimalHours * 3600);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

// Convert HH:MM:SS to seconds for calculations
const timeToSeconds = (timeString) => {
  if (!timeString) return 0;
  const parts = timeString.split(':').map(Number);
  return parts[0] * 3600 + parts[1] * 60 + (parts[2] || 0);
};

// Convert seconds to HH:MM:SS
const secondsToHMS = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

// Calculate productive time: Worked Time - Break Time - Idle Time
const calculateProductiveTime = (workedTime, breakTime, idleTime) => {
  const workedSeconds = timeToSeconds(workedTime);
  const breakSeconds = timeToSeconds(breakTime);
  const idleSeconds = timeToSeconds(idleTime);
  
  // Ensure productive time is not negative
  const productiveSeconds = Math.max(0, workedSeconds - breakSeconds - idleSeconds);
  return secondsToHMS(productiveSeconds);
};

// Calculate productive time in seconds (for sorting)
const getProductiveSeconds = (workedTime, breakTime, idleTime) => {
  const workedSeconds = timeToSeconds(workedTime);
  const breakSeconds = timeToSeconds(breakTime);
  const idleSeconds = timeToSeconds(idleTime);
  return Math.max(0, workedSeconds - breakSeconds - idleSeconds);
};

// New component: Member Dropdown Search
const MemberSearchDropdown = ({ 
  members = [], 
  onSearchChange, 
  onMemberSelect,
  value = '',
  placeholder = "Search by name or date..."
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredMembers, setFilteredMembers] = useState(members);
  const searchRef = useRef(null);

  useEffect(() => {
    setFilteredMembers(members);
  }, [members]);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    onSearchChange(inputValue);
    
    // Filter members based on input
    if (inputValue.trim() === '') {
      setFilteredMembers(members);
    } else {
      const filtered = members.filter(member =>
        member.name.toLowerCase().includes(inputValue.toLowerCase()) ||
        member.user_id.toString().includes(inputValue)
      );
      setFilteredMembers(filtered);
    }
    
    // Open dropdown when typing
    if (inputValue && !isOpen) {
      setIsOpen(true);
    }
  };

  const handleMemberClick = (member) => {
    onSearchChange(member.name);
    onMemberSelect(member);
    setIsOpen(false);
  };

  const handleClearSearch = () => {
    onSearchChange('');
    setFilteredMembers(members);
    setIsOpen(false);
  };

  const handleFocus = () => {
    if (value && filteredMembers.length > 0) {
      setIsOpen(true);
    }
  };

  const handleClickOutside = (e) => {
    if (searchRef.current && !searchRef.current.contains(e.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="member-search-wrapper" ref={searchRef}>
      <div className="search-input-wrapper">
        <span className="search-icon">🔍</span>
        <input
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={handleInputChange}
          onFocus={handleFocus}
          className="search-input"
        />
        {value && (
          <button 
            className="clear-search"
            onClick={handleClearSearch}
          >
            ✕
          </button>
        )}
      </div>
      
      {isOpen && filteredMembers.length > 0 && (
        <div className="member-dropdown">
          <div className="dropdown-header">
            <span className="member-count">{filteredMembers.length} member{filteredMembers.length !== 1 ? 's' : ''} found</span>
          </div>
          <div className="member-list">
            {filteredMembers.map((member, index) => (
              <div 
                key={`${member.user_id}-${index}`}
                className="member-item"
                onClick={() => handleMemberClick(member)}
              >
                <div className="member-avatar">
                  {member.name.charAt(0).toUpperCase()}
                </div>
                <div className="member-info">
                  <div className="member-name">{member.name}</div>
                  <div className="member-id">ID: {member.user_id}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {isOpen && filteredMembers.length === 0 && value && (
        <div className="member-dropdown">
          <div className="no-members-found">
            No members found for "{value}"
          </div>
        </div>
      )}
    </div>
  );
};

// New component: Modern Table with integrated filtering
const ModernReportTable = ({ 
  reports, 
  members = [],
  showFilters = true,
  onFilterChange,
  onSearchChange,
  onMemberSelect,
  onRowClick,
  onExportExcel,
  exportDisabled = false
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    search: ''
  });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    if (onFilterChange) onFilterChange(newFilters);
  };

  const handleSearchChange = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    if (onSearchChange) onSearchChange(value);
  };

  const handleMemberSelect = (member) => {
    if (onMemberSelect) onMemberSelect(member);
  };

  // Add productive time to each report
  const reportsWithProductiveTime = useMemo(() => {
    return reports.map(report => ({
      ...report,
      productiveTime: calculateProductiveTime(
        report.workedHours,
        report.breakTime,
        report.idleTime
      ),
      productiveSeconds: getProductiveSeconds(
        report.workedHours,
        report.breakTime,
        report.idleTime
      )
    }));
  }, [reports]);

  const sortedAndFilteredReports = useMemo(() => {
    let result = [...reportsWithProductiveTime];
    
    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(report => 
        report.name.toLowerCase().includes(searchLower) ||
        report.date.includes(searchLower)
      );
    }
    
    // Apply date filters
    if (filters.dateFrom) {
      result = result.filter(report => new Date(report.date) >= new Date(filters.dateFrom));
    }
    if (filters.dateTo) {
      result = result.filter(report => new Date(report.date) <= new Date(filters.dateTo));
    }
    
    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aValue, bValue;
        
        // Handle special cases
        if (sortConfig.key === 'productiveTime') {
          aValue = a.productiveSeconds;
          bValue = b.productiveSeconds;
        } else if (sortConfig.key.includes('Time') || sortConfig.key === 'workedHours') {
          // Convert HH:MM:SS to seconds for sorting
          aValue = timeToSeconds(a[sortConfig.key]);
          bValue = timeToSeconds(b[sortConfig.key]);
        } else if (sortConfig.key === 'productiveSeconds') {
          aValue = a.productiveSeconds;
          bValue = b.productiveSeconds;
        } else {
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return result;
  }, [reportsWithProductiveTime, filters, sortConfig]);

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '↕️';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="modern-report-container">
      {showFilters && (
        <div className="modern-filters-section">
          <div className="filter-header">
            <div className="filter-header-left">
              <h3>Filter Reports</h3>
              <div className="filter-count">
                Showing {sortedAndFilteredReports.length} of {reports.length} records
              </div>
            </div>
            <div className="filter-header-right">
              <button
                onClick={onExportExcel}
                className="export-excel-btn"
                disabled={exportDisabled || sortedAndFilteredReports.length === 0}
              >
                <span className="export-icon">📊</span>
                Export Excel
              </button>
            </div>
          </div>
          
          <div className="filter-controls">
            <div className="filter-group">
              <label>Search Members</label>
              <MemberSearchDropdown
                members={members}
                onSearchChange={handleSearchChange}
                onMemberSelect={handleMemberSelect}
                value={filters.search}
                placeholder="Search member name or ID..."
              />
            </div>
            
            <div className="filter-group">
              <label>Date Range</label>
              <div className="date-range-filters">
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  className="date-input"
                />
                <span className="date-range-separator">to</span>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  className="date-input"
                />
                {(filters.dateFrom || filters.dateTo) && (
                  <button 
                    className="clear-filters"
                    onClick={() => {
                      handleFilterChange('dateFrom', '');
                      handleFilterChange('dateTo', '');
                    }}
                  >
                    Clear Dates
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="modern-table-container">
        <div className="table-scroll-wrapper">
          <table className="modern-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('name')} className="sortable">
                  <div className="table-header-content">
                    Employee Name
                    <span className="sort-icon">{getSortIcon('name')}</span>
                  </div>
                </th>
                <th onClick={() => handleSort('date')} className="sortable">
                  <div className="table-header-content">
                    Date
                    <span className="sort-icon">{getSortIcon('date')}</span>
                  </div>
                </th>
                <th onClick={() => handleSort('workedHours')} className="sortable">
                  <div className="table-header-content">
                    Worked Hours
                    <span className="sort-icon">{getSortIcon('workedHours')}</span>
                  </div>
                </th>
                <th onClick={() => handleSort('breakTime')} className="sortable">
                  <div className="table-header-content">
                    Break Time
                    <span className="sort-icon">{getSortIcon('breakTime')}</span>
                  </div>
                </th>
                <th onClick={() => handleSort('idleTime')} className="sortable">
                  <div className="table-header-content">
                    Idle Time
                    <span className="sort-icon">{getSortIcon('idleTime')}</span>
                  </div>
                </th>
                <th onClick={() => handleSort('productiveTime')} className="sortable">
                  <div className="table-header-content">
                    Productive Time
                    <span className="sort-icon">{getSortIcon('productiveTime')}</span>
                  </div>
                </th>
                <th onClick={() => handleSort('productivity')} className="sortable">
                  <div className="table-header-content">
                    Productivity
                    <span className="sort-icon">{getSortIcon('productivity')}</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedAndFilteredReports.map((report, index) => (
                <tr 
                  key={`${report.user_id}-${report.date}`} 
                  className="table-row"
                  onClick={() => onRowClick && onRowClick(report)}
                >
                  <td>
                    <div className="employee-cell">
                      <div className="employee-avatar">
                        {report.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="employee-info">
                        <div className="employee-name">{report.name}</div>
                        <div className="employee-id">ID: {report.user_id}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="date-cell">
                      {new Date(report.date).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </div>
                  </td>
                  <td>
                    <div className="time-cell worked">
                      {report.workedHours}
                    </div>
                  </td>
                  <td>
                    <div className="time-cell break">
                      {report.breakTime}
                    </div>
                  </td>
                  <td>
                    <div className="time-cell idle">
                      {report.idleTime}
                    </div>
                  </td>
                  <td>
                    <div className="time-cell productive">
                      {report.productiveTime}
                    </div>
                  </td>
                  <td>
                    <div className="productivity-cell">
                      <div className="productivity-bar-container">
                        <div 
                          className="productivity-bar"
                          style={{ width: `${report.productivity}%` }}
                        ></div>
                      </div>
                      <span className="productivity-value">
                        {report.productivity}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {sortedAndFilteredReports.length === 0 && (
            <div className="no-data-message">
              <div className="no-data-icon">📊</div>
              <h3>No matching records found</h3>
              <p>Try adjusting your filters or search criteria</p>
            </div>
          )}
        </div>
      </div>
      
      <div className="table-footer">
        <div className="table-stats">
          <span className="stat-item">
            <strong>Total Hours:</strong> {calculateTotalHours(sortedAndFilteredReports)}
          </span>
          <span className="stat-item">
            <strong>Productive Hours:</strong> {calculateTotalProductiveHours(sortedAndFilteredReports)}
          </span>
          <span className="stat-item">
            <strong>Avg Productivity:</strong> {calculateAverageProductivity(sortedAndFilteredReports)}%
          </span>
        </div>
      </div>
    </div>
  );
};

// Helper functions
const calculateTotalHours = (reports) => {
  const totalSeconds = reports.reduce((acc, report) => {
    const [hours, minutes, seconds] = report.workedHours.split(':').map(Number);
    return acc + hours * 3600 + minutes * 60 + seconds;
  }, 0);
  
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
};

const calculateTotalProductiveHours = (reports) => {
  const totalSeconds = reports.reduce((acc, report) => {
    return acc + (report.productiveSeconds || 0);
  }, 0);
  
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
};

const calculateAverageProductivity = (reports) => {
  if (reports.length === 0) return 0;
  const total = reports.reduce((acc, report) => acc + report.productivity, 0);
  return Math.round(total / reports.length);
};

// Calculate efficiency ratio: Productive Time / Worked Time
const calculateEfficiencyRatio = (reports) => {
  if (reports.length === 0) return 0;
  
  const totalWorkedSeconds = reports.reduce((acc, report) => {
    return acc + timeToSeconds(report.workedHours);
  }, 0);
  
  const totalProductiveSeconds = reports.reduce((acc, report) => {
    return acc + (report.productiveSeconds || 0);
  }, 0);
  
  if (totalWorkedSeconds === 0) return 0;
  return Math.round((totalProductiveSeconds / totalWorkedSeconds) * 100);
};

// Main Reports Component
const Reports = () => {
  const { user } = useContext(AuthContext);
  const [allReports, setAllReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewMode, setViewMode] = useState('summary'); // 'summary' or 'detailed'
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const fetchReports = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const workspaces = await fetchWorkspaces(user.id);
      const adminWorkspaces = workspaces.filter(
        (w) => w.owner_id === user.id
      );

      const reportsResults = await Promise.all(
        adminWorkspaces.map((w) => fetchWorkspaceReports(w.id))
      );

      const flattened = reportsResults.flat().map((r) => ({
        user_id: r.user_id,
        name: r.employee_name,
        workedHours: hoursToHMS(+r.worked_hours || 0),
        breakTime: hoursToHMS(+r.break_hours || 0),
        idleTime: hoursToHMS(+r.idle_hours || 0),
        productivity: Math.min(100, +r.productivity || 0),
        date: r.date
          ? new Date(r.date).toISOString().split("T")[0]
          : "",
      }));

      setAllReports(flattened);
      setError("");
    } catch (err) {
      console.error("Failed to fetch reports:", err);
      setError("Failed to load reports. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  // Get unique members from all reports (excluding admin - the logged in user)
  const members = useMemo(() => {
    const uniqueMembers = [];
    const seenIds = new Set();
    
    allReports.forEach(report => {
      // Skip the admin (logged in user) - assuming admin user_id is different from member user_id
      // If your system identifies admin differently, adjust this condition
      if (!seenIds.has(report.user_id) && report.user_id !== user?.id) {
        seenIds.add(report.user_id);
        uniqueMembers.push({
          user_id: report.user_id,
          name: report.name
        });
      }
    });
    
    return uniqueMembers.sort((a, b) => a.name.localeCompare(b.name));
  }, [allReports, user?.id]);

  // Get summary reports (latest entry per user) with productive time, excluding admin
  const summaryReports = useMemo(() => {
    const latestMap = {};
    allReports.forEach((r) => {
      // Skip admin (logged in user)
      if (r.user_id === user?.id) return;
      
      if (
        !latestMap[r.user_id] ||
        new Date(r.date) > new Date(latestMap[r.user_id].date)
      ) {
        latestMap[r.user_id] = r;
      }
    });
    return Object.values(latestMap);
  }, [allReports, user?.id]);

  // Get all reports excluding admin
  const filteredAllReports = useMemo(() => {
    return allReports.filter(report => report.user_id !== user?.id);
  }, [allReports, user?.id]);

  // Get reports for selected employee
  const employeeReports = useMemo(() => {
    if (!selectedEmployee) return [];
    return filteredAllReports
      .filter(r => r.user_id === selectedEmployee.user_id)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [filteredAllReports, selectedEmployee]);

  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    setViewMode('detailed');
  };

  const handleBackToSummary = () => {
    setViewMode('summary');
    setSelectedEmployee(null);
  };

  const exportToExcel = () => {
    const data = viewMode === 'detailed' ? employeeReports : summaryReports;
    if (!data.length) {
      alert("No data to export");
      return;
    }

    // Add productive time to export data
    const exportData = data.map((r) => ({
      Name: r.name,
      Date: r.date,
      "Worked Hours": r.workedHours,
      "Break Time": r.breakTime,
      "Idle Time": r.idleTime,
      "Productive Time": calculateProductiveTime(r.workedHours, r.breakTime, r.idleTime),
      Productivity: `${r.productivity}%`,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Reports");
    XLSX.writeFile(wb, `${viewMode}_reports_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  if (loading) {
    return (
      <div className="reports-container">
        
      </div>
    );
  }

  if (error) {
    return (
      <div className="reports-container">
        <div className="error-message">
          <div className="error-icon">!</div>
          <h3>Error Loading Reports</h3>
          <p>{error}</p>
          <button onClick={fetchReports} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-container">
      <header className="reports-header">
        <div className="header-content">
          <h1 className="reports-title">
            {viewMode === 'detailed' ? (
              <>
                <button onClick={handleBackToSummary} className="back-arrow-btn">
                  ←
                </button>
                {selectedEmployee?.name}'s Activity Report
              </>
            ) : (
              "Team Productivity Dashboard"
            )}
          </h1>
          <p className="subtitle">
            {viewMode === 'detailed' 
              ? "Detailed daily activity breakdown" 
              : "Monitor team productivity and work patterns"}
          </p>
        </div>
        
        <div className="header-actions">
          <div className="view-toggle">
            <button 
              className={`toggle-btn ${viewMode === 'summary' ? 'active' : ''}`}
              onClick={handleBackToSummary}
            >
              Summary View
            </button>
            <button 
              className={`toggle-btn ${viewMode === 'detailed' ? 'active' : ''}`}
              disabled={!selectedEmployee}
            >
              Detailed View
            </button>
          </div>
        </div>
      </header>

      <main className="reports-main">
        {viewMode === 'summary' ? (
          <>
            <div className="dashboard-summary">
              <div className="summary-card total-employees">
                <div className="summary-icon">👥</div>
                <div className="summary-content">
                  <h3>{summaryReports.length}</h3>
                  <p>Team Members</p>
                </div>
              </div>
              
              <div className="summary-card avg-productivity">
                <div className="summary-icon">📈</div>
                <div className="summary-content">
                  <h3>
                    {summaryReports.length > 0 
                      ? Math.round(summaryReports.reduce((sum, r) => sum + r.productivity, 0) / summaryReports.length)
                      : 0}%
                  </h3>
                  <p>Avg Productivity</p>
                </div>
              </div>
              
              <div className="summary-card avg-productive-time">
                <div className="summary-icon">⚡</div>
                <div className="summary-content">
                  <h3>
                    {calculateAverageProductiveTime(summaryReports)}
                  </h3>
                  <p>Avg Productive Time</p>
                </div>
              </div>
              
              <div className="summary-card efficiency-ratio">
                <div className="summary-icon">🎯</div>
                <div className="summary-content">
                  <h3>
                    {calculateEfficiencyRatio(summaryReports)}%
                  </h3>
                  <p>Efficiency Ratio</p>
                </div>
              </div>
            </div>

            <ModernReportTable
              reports={summaryReports}
              members={members}
              showFilters={true}
              onRowClick={handleEmployeeSelect}
              onMemberSelect={handleEmployeeSelect}
              onExportExcel={exportToExcel}
              exportDisabled={summaryReports.length === 0}
            />
          </>
        ) : (
          <div className="detailed-view">
            {selectedEmployee && (
              <div className="employee-header-card">
                <div className="employee-avatar-large">
                  {selectedEmployee.name.charAt(0).toUpperCase()}
                </div>
                <div className="employee-header-info">
                  <h2>{selectedEmployee.name}</h2>
                  <p>Employee ID: {selectedEmployee.user_id}</p>
                  <div className="employee-stats">
                    <span className="stat-badge">
                      <strong>Last Activity:</strong> {selectedEmployee.date}
                    </span>
                    <span className="stat-badge">
                      <strong>Productivity:</strong> {selectedEmployee.productivity}%
                    </span>
                    <span className="stat-badge">
                      <strong>Worked Hours:</strong> {selectedEmployee.workedHours}
                    </span>
                    <span className="stat-badge">
                      <strong>Productive Time:</strong> {calculateProductiveTime(
                        selectedEmployee.workedHours,
                        selectedEmployee.breakTime,
                        selectedEmployee.idleTime
                      )}
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            <ModernReportTable
              reports={employeeReports}
              members={members}
              showFilters={true}
              onMemberSelect={handleEmployeeSelect}
              onExportExcel={exportToExcel}
              exportDisabled={employeeReports.length === 0}
            />
          </div>
        )}
      </main>
    </div>
  );
};

const calculateAverageWorkedHours = (reports) => {
  if (reports.length === 0) return '0h 0m';
  
  const totalSeconds = reports.reduce((acc, report) => {
    const [hours, minutes] = report.workedHours.split(':').map(Number);
    return acc + hours * 3600 + minutes * 60;
  }, 0);
  
  const avgSeconds = totalSeconds / reports.length;
  const hours = Math.floor(avgSeconds / 3600);
  const minutes = Math.floor((avgSeconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
};

const calculateAverageProductiveTime = (reports) => {
  if (reports.length === 0) return '0h 0m';
  
  const totalSeconds = reports.reduce((acc, report) => {
    return acc + getProductiveSeconds(
      report.workedHours,
      report.breakTime,
      report.idleTime
    );
  }, 0);
  
  const avgSeconds = totalSeconds / reports.length;
  const hours = Math.floor(avgSeconds / 3600);
  const minutes = Math.floor((avgSeconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
};

export default Reports;